<template>
  <div class="references-tab">
    <reference-filters
      :filtering-input.sync="filteringInputModel"
      :available-tags="review.tags"
    />
    <div class="reference-list">
      <div class="search-and-result-count">
        <search-bar
          v-model="searchQueryModel"
          class="search-bar"
          :debounce="0"
        />
        <div
          v-if="!loading && hasReferences"
          class="text-body-2 grey--text font-weight-light"
        >
          {{ pageCount }} of {{ totalCountFormatted }} results found
        </div>
      </div>
      <infinite-scroll-detector
        class="infinite-scroll-detector"
        use-first-child
        @load-more="onLoadMore"
      >
        <div class="scroll-view mt-5 pt-1">
          <reference-card-list
            v-if="hasReferences"
            :review="review"
            :references="references"
            :highlight-terms="highlights"
            :researcher="researcher"
            :disable-actions-for-references="
              disableActionsForReferences
            "
            @edit="$emit('edit', $event)"
            @set-reference-tags="$emit('set-reference-tags', $event)"
            @download-full-text="$emit('download-full-text', $event)"
            @delete-full-text="$emit('delete-full-text', $event)"
            @create-screening-vote="
              $emit('create-screening-vote', $event)
            "
            @change-screening-vote="
              $emit('change-screening-vote', $event)
            "
            @revoke-screening-vote="
              $emit('revoke-screening-vote', $event)
            "
            @resolve-screening-conflict="
              $emit('resolve-screening-conflict', $event)
            "
            @change-screening-outcome="
              $emit('change-screening-outcome', $event)
            "
            @create-full-text-review-vote="
              $emit('create-full-text-review-vote', $event)
            "
            @change-full-text-review-vote="
              $emit('change-full-text-review-vote', $event)
            "
            @revoke-full-text-review-vote="
              $emit('revoke-full-text-review-vote', $event)
            "
            @resolve-full-text-review-conflict="
              $emit('resolve-full-text-review-conflict', $event)
            "
            @change-full-text-review-outcome="
              $emit('change-full-text-review-outcome', $event)
            "
            @extract="$emit('extract', $event)"
            @extraction-consensus-review="
              $emit('extraction-consensus-review', $event)
            "
            @mark-as-duplicate="$emit('mark-as-duplicate', $event)"
          />
          <reference-card-list-skeleton-loader v-else-if="loading" />
          <loading-more-indicator
            v-if="hasReferences"
            :loading="loadingMore"
            :has-next-page="hasNextPage"
            @load-more="onLoadMore"
          />
        </div>
        <reference-tab-page-placeholder
          v-if="!hasReferences && !loading"
          @clear-all-filters="clearAllFilters"
        />
      </infinite-scroll-detector>
    </div>
  </div>
</template>

<script>
import ReferenceFilters from './ReferenceFilters/ReferenceFilters';
import SearchBar from '@/components/Common/SearchBar';
import InfiniteScrollDetector from '@/components/Common/InfiniteScrollDetector';
import LoadingMoreIndicator from '@/components/Common/LoadingMoreIndicator';
import ReferenceCardList from './ReferenceCard/ReferenceCardList';
import ReferenceCardListSkeletonLoader from './ReferenceCard/ReferenceCardListSkeletonLoader';
import ReferenceTabPagePlaceholder from './ReferencesTabPagePlaceholder';
import {
  Review,
  Reference,
  Researcher,
  ReferenceFilteringInputDto,
} from '@/models';
import { typeHelpers } from '@/modules';
import { cloneDeep } from 'lodash';

export default {
  name: 'ReferencesTab',
  components: {
    ReferenceFilters,
    SearchBar,
    InfiniteScrollDetector,
    LoadingMoreIndicator,
    ReferenceCardList,
    ReferenceCardListSkeletonLoader,
    ReferenceTabPagePlaceholder,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    researcher: {
      type: Researcher,
      required: true,
    },
    references: {
      type: Array,
      default: () => [],
      validator: (val) => typeHelpers.isArrayOfType(val, Reference),
    },
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    highlights: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: null,
    },
    hasNextPage: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    disableActionsForReferences: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'update:filteringInput',
    'load-more',
    're-label',
    'edit',
    'set-reference-tags',
    'download-full-text',
    'delete-full-text',
    'create-screening-vote',
    'change-screening-vote',
    'revoke-screening-vote',
    'resolve-screening-conflict',
    'change-screening-outcome',
    'create-full-text-review-vote',
    'change-full-text-review-vote',
    'revoke-full-text-review-vote',
    'resolve-full-text-review-conflict',
    'change-full-text-review-outcome',
    'extract',
    'extraction-consensus-review',
    'mark-as-duplicate',
  ],
  computed: {
    searchQueryModel: {
      get: function () {
        return this.filteringInput.searchQuery ?? '';
      },
      set: function (value) {
        const filteringInput = cloneDeep(this.filteringInput);
        filteringInput.searchQuery = value !== '' ? value : null;
        this.filteringInputModel = filteringInput;
      },
    },
    filteringInputModel: {
      get: function () {
        return this.filteringInput;
      },
      set: function (value) {
        this.$emit('update:filteringInput', value);
      },
    },
    hasReferences() {
      return this.references.length > 0;
    },
    pageCount() {
      return this.references?.length ?? 0;
    },
    totalCountFormatted() {
      return this.totalCount?.toLocaleString();
    },
  },
  methods: {
    onLoadMore() {
      this.$emit('load-more');
    },
    clearAllFilters() {
      this.filteringInputModel = new ReferenceFilteringInputDto();
    },
  },
};
</script>

<style lang="scss" scoped>
.references-tab {
  display: grid;
  grid-template-columns: minmax(0, 400px) 1fr;

  .reference-list {
    padding: 24px 0px 24px 0px;
    height: calc(100vh - 117px);
  }

  .infinite-scroll-detector {
    height: calc(100% - 39px);
  }

  .scroll-view,
  .search-and-result-count {
    padding-left: 36px;
    padding-right: 36px;
  }

  .scroll-view {
    max-height: 100%;
    overflow-y: scroll;
  }

  .search-and-result-count {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .search-bar {
      max-width: 550px;
    }
  }
}
</style>
